import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Tooltip,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import axios from "axios";
import Images from "../../../utils/Images";
import { CategoryFilterChip } from "../../../components/FilterChip";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { KeyboardArrowLeftOutlined, KeyboardArrowRightOutlined } from "@mui/icons-material";

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function OnlineItemSales() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));

  // data
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // selection
  const [selectedProduct, setSelectedProduct] = useState(null);

  // sale header
  const [platform, setPlatform] = useState("ZOMATO");
  const [date, setDate] = useState(todayYYYYMMDD());

  // current item config
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);

  /**
   * addonSelections structure (supports single & multiple):
   * {
   *   [addonId]: {
   *     addonId,
   *     addonTitle,
   *     isOptional,
   *     isMulti,
   *     values: [
   *       { addonValueId, addonValueTitle }
   *     ]
   *   }
   * }
   */
  const [addonSelections, setAddonSelections] = useState({});

  const [cartItems, setCartItems] = useState([]);
  const [posting, setPosting] = useState(false);

  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];
    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: { _id: cat._id, name: cat.name, description: cat.description },
      }))
    );
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API.BASE_URL}product/getProductsWithAddons`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { outletId: outletData._id, orderType: "PICKUP" },
      });

      const fetchedCategories = response?.data?.data?.categories || [];
      setCategories(fetchedCategories);

      const flattened = flattenCategoriesToProducts(fetchedCategories);
      setAllProducts(flattened);
    } catch (e) {
      console.error("Error fetching products with addons:", e);
      toast.error("Failed to fetch products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const products = useMemo(() => {
    const search = (searchQuery || "").toLowerCase().trim();
    return allProducts.filter((p) => {
      const categoryOk = selectedCategoryId ? p?.category?._id === selectedCategoryId : true;
      if (!categoryOk) return false;

      if (!search) return true;

      const nameMatch = p.name?.toLowerCase().includes(search);
      const descMatch = p.description?.toLowerCase().includes(search);
      const keywordsMatch = p.keywords?.toLowerCase().includes(search);
      return nameMatch || descMatch || keywordsMatch;
    });
  }, [allProducts, searchQuery, selectedCategoryId]);

  const getValidAddonValues = (addon) => {
    if (!addon?.values?.length) return [];
    return addon.values.filter((v) => {
      const isActiveOk = v?.status !== "INACTIVE" || v?.isDefault;
      const notExcluded = !v?.excludedOutlets?.includes(outletData?._id) || v?.isDefault;
      const notIconInfo = !v?.iconInfo;
      return isActiveOk && notExcluded && notIconInfo;
    });
  };

  const isAddonMultiSelect = (addon) => {
    return (
      addon?.isMultipleSelect === true
    );
  };

  const buildDefaultAddonSelections = (product) => {
    const next = {};
    if (!product?.hasAddon || !Array.isArray(product?.addons)) return next;

    const visibleAddons = product.addons
      .filter((a) => !a?.excludedOutlets?.includes(outletData?._id));

    for (const addon of visibleAddons) {
      const required = addon?.isOptional === false;
      if (!required) continue;

      const validValues = getValidAddonValues(addon);
      if (!validValues.length) continue;

      const defaultValue = validValues.find((v) => v?.isDefault) || validValues[0];
      const multi = isAddonMultiSelect(addon);

      next[addon._id] = {
        addonId: addon._id,
        addonTitle: addon.title,
        isOptional: addon?.isOptional !== false,
        isMulti: multi,
        values: [
          {
            addonValueId: defaultValue._id,
            addonValueTitle: defaultValue.value,
          },
        ],
      };
    }

    return next;
  };

  useEffect(() => {
    // reset builder fields when product changes
    setQty(1);
    setDiscount(0);

    const p =
      selectedProduct?.newOfferPrice ??
      selectedProduct?.offerPrice ??
      selectedProduct?.defaultOfferPrice ??
      selectedProduct?.price ??
      0;

    setPrice(num(p));

    // set defaults for REQUIRED addons and lock "unselect" for required
    const defaults = buildDefaultAddonSelections(selectedProduct);
    setAddonSelections(defaults);
  }, [selectedProduct]);

  const currentTotal = useMemo(
    () => Math.max(0, num(qty) * num(price) - num(discount)),
    [qty, price, discount]
  );

  const cartGrandTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + num(item.total), 0),
    [cartItems]
  );

  const isValueSelected = (addonId, valueId) => {
    const entry = addonSelections?.[addonId];
    if (!entry?.values?.length) return false;
    return entry.values.some((x) => x.addonValueId === valueId);
  };

  const canUnselectValue = (addon, addonId) => {
    const required = addon?.isOptional === false;
    if (!required) return true;

    // required => cannot end up with 0 selections
    const selectedCount = addonSelections?.[addonId]?.values?.length || 0;
    return selectedCount > 1;
  };

  const toggleAddonValue = (addon, value) => {
    const addonId = addon._id;
    const multi = isAddonMultiSelect(addon);
    const required = addon?.isOptional === false;

    setAddonSelections((prev) => {
      const existing = prev?.[addonId];
      const existingValues = existing?.values || [];
      const already = existingValues.some((x) => x.addonValueId === value._id);

      // base entry
      const base = {
        addonId,
        addonTitle: addon.title,
        isOptional: !required,
        isMulti: multi,
      };

      // SINGLE SELECT
      if (!multi) {
        // If clicking selected value:
        if (already) {
          // optional -> allow unselect (clear)
          if (!required) {
            const { [addonId]: _, ...rest } = prev;
            return rest;
          }
          // required -> do nothing (cannot unselect)
          return prev;
        }

        // else set as selected (switch)
        return {
          ...prev,
          [addonId]: {
            ...base,
            values: [
              {
                addonValueId: value._id,
                addonValueTitle: value.value,
              },
            ],
          },
        };
      }

      // MULTI SELECT
      if (already) {
        // attempt to unselect
        if (!canUnselectValue(addon, addonId)) {
          // required + last item => block unselect
          return prev;
        }

        const nextValues = existingValues.filter((x) => x.addonValueId !== value._id);

        // optional: if none remain, remove key entirely
        if (!required && nextValues.length === 0) {
          const { [addonId]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [addonId]: {
            ...base,
            values: nextValues,
          },
        };
      }

      return {
        ...prev,
        [addonId]: {
          ...base,
          values: [
            ...existingValues,
            {
              addonValueId: value._id,
              addonValueTitle: value.value,
            },
          ],
        },
      };
    });
  };

  const validateCurrentItem = () => {
    if (!selectedProduct?._id) return "Please select a product.";
    if (selectedProduct?.isExhausted) return "Product is exhausted.";
    if (num(qty) <= 0) return "Quantity must be greater than 0.";

    if (selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons)) {
      const requiredAddons = selectedProduct.addons
        .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
        .filter((a) => a?.isOptional === false);

      for (const addon of requiredAddons) {
        const validValues = getValidAddonValues(addon);
        if (!validValues.length) continue;

        const entry = addonSelections?.[addon._id];
        const selectedCount = entry?.values?.length || 0;
        if (selectedCount === 0) return `Select required addon: ${addon.title}`;
      }
    }

    return null;
  };

  const handleAddItemToCart = () => {
    const err = validateCurrentItem();
    if (err) return toast.error(err);

    const addonsArray = Object.values(addonSelections).flatMap((entry) =>
      (entry.values || []).map((v) => ({
        addonId: entry.addonId,
        addonValueId: v.addonValueId,
        addonTitle: entry.addonTitle,
        addonValueTitle: v.addonValueTitle,
      }))
    );

    const payloadItem = {
      platform,
      date,
      product: selectedProduct._id,
      productName: selectedProduct.name,
      addons: addonsArray,
      quantity: num(qty),
      price: num(price),
      discount: num(discount),
      total: num(currentTotal),
    };

    setCartItems((prev) => [...prev, payloadItem]);
    toast.success("Item added to sale.");
  };

  const removeCartItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
    toast.info("Item removed.");
  };

  const submitSale = async () => {
    if (!cartItems.length) return toast.error("Add at least 1 item to submit.");
    const toastId = toast.loading("Posting sale...");
    try {
      setPosting(true);
      for (const item of cartItems) {
        const body = {
          platform: item.platform,
          date: item.date,
          product: item.product,
          outlet: outletData?._id,
          addons: item.addons,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount,
          total: item.total,
        };
        await axios.post(`${API.BASE_URL}third-party-sales`, body, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
      }
      setCartItems([]);
      toast.update(toastId, {
        render: "Third-party sale posted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
      navigate("/online-sales-list");
    } catch (e) {
      const msg = e?.response?.data?.error || "Posting failed";
      toast.update(toastId, {
        render: msg,
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => {
    getCategoriesProductsList();
  }, []);

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#f5f6f8" }}>
      <Grid container sx={{ height: "100%", pt: "100px" }}>
        <Grid item md={7} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #eee", background: "#fff" }}>
              <Stack flexDirection={'row'} alignItems={'center'} mb={1}>
                <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="medium" />
                <Typography sx={{ fontSize: 21, fontWeight: 800 }}>Products</Typography>
              </Stack>

              <TextField
                fullWidth
                size="small"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}
              />

              <Grid container spacing={1} sx={{ mt: 1.5, justifyContent: "center" }}>
                {categories
                  .filter((category) => outletData.isEventOutlet || !category?.isEventCategory)
                  .map((category) => {
                    const isSelected = selectedCategoryId === category._id;
                    return (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={category._id}>
                        <CategoryFilterChip
                          filterText={category.name}
                          isSelected={isSelected}
                          onClick={() => setSelectedCategoryId(isSelected ? null : category._id)}
                        />
                      </Grid>
                    );
                  })}

                {selectedCategoryId && (
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Chip
                      label="Clear"
                      variant="outlined"
                      onClick={() => setSelectedCategoryId(null)}
                      sx={{ width: "100%", borderRadius: 999 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ p: 2, height: "calc(100% - 140px)", overflow: "auto" }}>
              {loadingProducts ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress sx={{ color: "#111" }} />
                  <Typography sx={{ mt: 1, opacity: 0.7 }}>Fetching products...</Typography>
                </Box>
              ) : products.length ? (
                <Grid container spacing={2} justifyContent={"center"}>
                  {products.map((product) => {
                    const active = selectedProduct?._id === product._id;
                    return (
                      <Grid item xs={12} sm={6} md={6} lg={4} key={product._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            borderColor: active ? "#00A4E2" : "#eee",
                            boxShadow: active ? "0 0 0 2px rgba(0,164,226,0.25)" : "none",
                            backgroundColor: product.isExhausted ? "#FFF4F4" : "#fff",
                          }}
                        >
                          <CardActionArea onClick={() => setSelectedProduct(product)}>
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Avatar
                                  variant="rounded"
                                  src={product.displayImageUrl || product.displayImage}
                                  alt={product.name}
                                  imgProps={{
                                    onError: (e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = Images.product;
                                    },
                                  }}
                                  sx={{ width: 52, height: 52, borderRadius: 2 }}
                                />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 800,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      opacity: 0.7,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.category?.name || "-"}
                                  </Typography>
                                </Box>

                                {product.isExhausted ? (
                                  <Tooltip title="Exhausted">
                                    <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography sx={{ textAlign: "center", py: 6, opacity: 0.7 }}>
                  No products available.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item md={5} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                px: 2,
                pt: 2,
                pb: 1.5,
                borderBottom: "1px solid #eee",
                background:
                  "linear-gradient(180deg, rgba(0,164,226,0.10) 0%, rgba(0,0,0,0) 60%)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    bgcolor: "rgba(0,164,226,0.15)",
                    color: "#00A4E2",
                    fontWeight: 900,
                  }}
                >
                  <ShoppingCartOutlinedIcon fontSize="small" />
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, lineHeight: 1.1 }}>
                    Online Sale
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                    Third-party • {outletData?.name || "Outlet"}
                  </Typography>
                </Box>

                <Chip
                  label={`₹${cartGrandTotal.toFixed(2)}`}
                  sx={{
                    ml: "auto",
                    fontWeight: 900,
                    bgcolor: "rgba(0,164,226,0.12)",
                    color: "#006B96",
                    borderRadius: 999,
                  }}
                />
              </Box>

              <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="platform-label">Platform</InputLabel>
                  <Select
                    labelId="platform-label"
                    value={platform}
                    label="Platform"
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <MenuItem value="ZOMATO">ZOMATO</MenuItem>
                    <MenuItem value="SWIGGY">SWIGGY</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  fullWidth
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: todayYYYYMMDD(),
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ px: 2, py: 2, overflow: "auto", flex: 1 }}>
              {!selectedProduct ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ fontSize: 13, opacity: 0.75, mb: 1 }}>
                    Select a product to start
                  </Typography>
                  <img
                    src={Images.noProductSelected}
                    alt="product-not-selected"
                    style={{ maxWidth: 220, opacity: 0.9 }}
                  />
                </Box>
              ) : (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: "#e9eef3",
                    background: "linear-gradient(180deg, #ffffff 0%, #fafcff 100%)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 1.5, display: "flex", gap: 1.2, alignItems: "center" }}>
                    <Avatar
                      variant="rounded"
                      src={selectedProduct.displayImageUrl || selectedProduct.displayImage}
                      alt={selectedProduct.name}
                      imgProps={{
                        onError: (e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = Images.product;
                        },
                      }}
                      sx={{ width: 54, height: 54, borderRadius: 2 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 900 }} noWrap>
                        {selectedProduct.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                        {selectedProduct.category?.name || "-"}
                      </Typography>
                    </Box>

                    {selectedProduct.isExhausted ? (
                      <Tooltip title="Exhausted">
                        <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label="Selected"
                        sx={{
                          fontWeight: 800,
                          bgcolor: "rgba(0,164,226,0.12)",
                          color: "#006B96",
                        }}
                      />
                    )}
                  </Box>

                  {/* Builder Inputs */}
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.1 }}>
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>
                          Quantity
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={qty}
                          onChange={(e) => setQty(e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>
                          Price
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>
                          Discount
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={discount}
                          onChange={(e) => setDiscount(e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          background:
                            "linear-gradient(180deg, rgba(0,164,226,0.10) 0%, rgba(0,0,0,0) 120%)",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>
                          Total
                        </Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>
                          ₹{currentTotal.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    {selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons) ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 900, mb: 1 }}>
                          Addons
                        </Typography>

                        <Stack spacing={1}>
                          {selectedProduct?.addons
                            .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
                            .map((addon) => {
                              const values = getValidAddonValues(addon);
                              if (!values.length) return null;
                              const multi = isAddonMultiSelect(addon);

                              return (
                                <Box
                                  key={addon?._id}
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2,
                                    border: "1px solid #eef2f6",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                                      {addon?.title}
                                    </Typography>

                                    {!addon?.isOptional && (
                                      <Chip
                                        size="small"
                                        label="Required"
                                        sx={{ height: 20, fontSize: 11 }}
                                      />
                                    )}

                                    {multi ? (
                                      <Chip
                                        size="small"
                                        label="Multi"
                                        sx={{ height: 20, fontSize: 11, ml: "auto" }}
                                        variant="outlined"
                                      />
                                    ) : null}
                                  </Box>

                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {values?.map((v) => {
                                      const active = isValueSelected(addon?._id, v?._id);

                                      return (
                                        <Chip
                                          key={v?._id}
                                          label={`${v?.value}${num(v?.price) ? `  +₹${v?.price}` : ""}`}
                                          onClick={() => toggleAddonValue(addon, v)}
                                          variant={active ? "filled" : "outlined"}
                                          sx={{
                                            borderRadius: 999,
                                            ...(active
                                              ? { backgroundColor: "#00A4E2", color: "#fff" }
                                              : { borderColor: "#00A4E2", color: "#00A4E2" }),
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>

                                  {!addon?.isOptional ? (
                                    <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 1 }}>
                                      Required addon: at least one value must remain selected.
                                    </Typography>
                                  ) : null}
                                </Box>
                              );
                            })}
                        </Stack>
                      </Box>
                    ) : null}

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddItemToCart}
                      sx={{
                        mt: 1.6,
                        borderRadius: 2,
                        backgroundColor: "#111",
                        py: 1.1,
                        fontWeight: 900,
                        textTransform: "none",
                      }}
                    >
                      Add Item
                    </Button>
                  </Box>
                </Card>
              )}

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
                    Sale Items <span style={{ opacity: 0.6 }}>({cartItems.length})</span>
                  </Typography>
                </Box>

                {cartItems.length === 0 ? (
                  <Typography sx={{ fontSize: 13, opacity: 0.7 }}>No items added yet.</Typography>
                ) : (
                  <Stack spacing={2}>
                    {cartItems.map((item, idx) => (
                      <Box
                        key={`${item.product}-${idx}`}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: "1px solid #e6ecf2",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Top Row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Box sx={{ flex: 1, pr: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
                              {item.productName}
                            </Typography>

                            <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                              Qty: {item.quantity}
                            </Typography>
                          </Box>

                          <IconButton size="small" onClick={() => removeCartItem(idx)} sx={{ mt: -0.5 }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Price Section */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                            Price ₹{item.price}  •  Discount ₹{item.discount}
                          </Typography>

                          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                            ₹{item.total}
                          </Typography>
                        </Box>

                        {/* Addons Section */}
                        {item.addons?.length ? (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1,
                              borderRadius: 2,
                              backgroundColor: "#f9fafb",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {item.addons.map((a) => (
                              <Chip
                                key={`${a.addonId}-${a.addonValueId}`}
                                size="small"
                                label={`${a.addonTitle}: ${a.addonValueTitle}`}
                                sx={{ fontSize: 11, bgcolor: "#ffffff" }}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                p: 2,
                borderTop: "1px solid #eee",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  mb: 1.2,
                  p: 1.2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  border: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>Grand Total</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>₹{cartGrandTotal.toFixed(2)}</Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={posting || cartItems.length === 0}
                onClick={submitSale}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#00A4E2",
                  py: 1.1,
                  fontWeight: 900,
                  textTransform: "none",
                }}
              >
                {posting ? "Posting..." : "Submit Sale"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default OnlineItemSales;